import * as borogove from "borogove";
import DOMPurify from "dompurify";
import Fuse from "fuse.js";
import QuillInner from "quill";
import { CircularProgress, Divider, Tabs, Tab } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Mention } from "quill-mention";
import { Quill } from "./Quill";
import * as linkify from "linkifyjs";
import Linkify from "linkify-react";
import { Virtuoso, VirtuosoHandle } from "react-virtuoso";
import {
	useNavigate,
	useLocation,
	useSearchParams,
	useOutletContext,
	useAsyncValue,
	Outlet,
} from "react-router";
import {
	MutableRefObject,
	useState,
	useEffect,
	useMemo,
	useRef,
	useCallback,
	useContext,
	useId,
} from "react";
import { uuidv7 } from "uuidv7";
import {
	Button,
	AttachmentButton,
	AddUserButton,
	Avatar,
	ChatContainer,
	ConversationHeader,
	MessageGroup,
	Message,
	MessageList,
	MessageInput,
	SendButton,
	TypingIndicator,
} from "@chatscope/chat-ui-kit-react";
import {
	faCircleXmark,
	faPencil,
	faPhone,
	faVideo,
	faAnglesDown,
	faCircleInfo,
} from "@fortawesome/free-solid-svg-icons";
import { useThrottle } from "../hooks/use-throttle";

import SpoolIcon from "../spool.svg?react";
import { AvatarWithPlaceholder } from "./AvatarWithPlaceholder";
import { AwaitOnce } from "./AwaitOnce";
import { MessageBubble } from "./MessageBubble";
import { MessageMenuInChat } from "./MessageMenuInChat";
import { CallMessage } from "./CallMessage";
import { CommandMenu } from "./CommandMenu";
import { CommandSession } from "./CommandSession";
import { SettingsContext } from "./Settings";
import {
	useEffectEvent,
	membersForMessages,
	Attachment,
	ChatExtras,
	href,
} from "../util";

borogove.Config.relativeHashUri = true;
DOMPurify.setConfig({
	ADD_TAGS: ["#document-fragment"],
	FORBID_TAGS: ["style", "form", "input", "select"],
	IN_PLACE: true,
});
linkify.registerCustomProtocol("xmpp", true);
linkify.registerCustomProtocol("tel", true);
linkify.registerCustomProtocol("sms", true);
linkify.registerCustomProtocol("bitcoin", true);
linkify.registerCustomProtocol("bitcoincash", true);
linkify.registerCustomProtocol("monero", true);
linkify.registerCustomProtocol("ethereum", true);

const subjectBoxId = uuidv7();

type Group = {
	senderId: string;
	threadId: string | null;
	incoming: boolean;
	backInTime: boolean;
	type: borogove.MessageType;
	messages: borogove.ChatMessage[];
};

export const ChatInner = () => {
	const { enterIsSend, richTextToolbar, messageRequests } =
		useContext(SettingsContext);
	const navigate = useNavigate();
	const location = useLocation();
	const [searchParams] = useSearchParams();
	const outletContext = useOutletContext<{
		active: boolean;
		activeChat: borogove.Chat;
		client: borogove.Client;
		getChatExtras: (id: string) => ChatExtras | undefined;
		setChatExtras: (id: string, d: Partial<ChatExtras>) => void;
		setPopupMember: (member: borogove.Member) => void;
		innerChatRef: MutableRefObject<{
			addMention: (member: borogove.Member) => void;
		}>;
		isInvite: boolean;
		persistence: borogove.persistence.KeyValueStore & borogove.Persistence;
		typingMembers: Array<borogove.Member>;
	}>();
	const {
		active,
		activeChat,
		client,
		getChatExtras,
		setChatExtras,
		setPopupMember,
		innerChatRef,
		isInvite,
		persistence,
		typingMembers,
	} = outletContext;

	const [commandSessions, setCommandSessions] = useState<
		Promise<borogove.CommandSession>[]
	>([]);
	const [tabNames, setTabNames] = useState<string[]>([]);
	const [tabIndex, setTabIndex] = useState(0);
	const [attachments, setAttachments] = useState<Attachment[]>([]);
	const [attachmentProgress, setAttachmentProgress] = useState(false);
	const [currentThread, setCurrentThread] = useState<string | undefined>();
	const [editing, setEditing] = useState<borogove.ChatMessage | null>(null);
	const [requestToSpeakSent, setRequestToSpeakSent] = useState(false);
	const [replyTo, setReplyTo] = useState<borogove.ChatMessage | null>(null);
	const [editChatName, setEditChatName] = useState(false);
	const [editSubject, setEditSubject] = useState(false);
	const [inputEmpty, setInputEmpty] = useState(true);
	const [virtuosoGeneration, setVirtuosoGeneration] = useState(0);
	const [wallpaper, setWallpaper] = useState<string | undefined>();
	const scrollTop = useRef<string | null>(null);
	const inputRef = useRef<QuillInner>();
	const messageListContainer = useRef<HTMLDivElement | null>(null);
	const messageList = useRef<VirtuosoHandle | null>(null);
	const {
		messages: initialMessages,
		members: initialMembers,
		readUpTo,
		invitedBy,
	} = useAsyncValue() as {
		messages: borogove.ChatMessage[];
		members: Map<string, borogove.Member>;
		readUpTo: borogove.ChatMessage;
		invitedBy: borogove.Member | null;
	};

	const [messages, setMessages] = useState(initialMessages);
	const [members, setMembers] = useState(initialMembers);
	const [initialTop, setInitialTop] = useState(initialMessages[0]);
	const loading = useRef(false);
	const firstMessage = useRef(messages[0]);
	const lastMessage = useRef(messages.at(-1));
	const groups: (Group | "readUpToMarker")[] = useMemo(
		() =>
			messages.reduce(
				(groups, message, idx, messages) => {
					const lastGroup = groups.at(-1);
					const penultimateGroup = groups.at(-2);
					const lastRealGroup: Group | undefined =
						lastGroup === "readUpToMarker"
							? penultimateGroup === "readUpToMarker"
								? undefined
								: penultimateGroup
							: lastGroup;
					const markerGroup: Array<"readUpToMarker"> =
						readUpTo &&
						message.canReplace(readUpTo) &&
						idx + 1 < messages.length
							? ["readUpToMarker"]
							: [];
					// There is a group less than 5 messages from the same sender and thread not more than 2 minutes long
					if (
						lastGroup &&
						lastGroup !== "readUpToMarker" &&
						lastGroup.messages.length < 5 &&
						(message.moderationReason() ||
							lastGroup.senderId === message.senderId) &&
						lastGroup.threadId === message.threadId &&
						lastGroup.type === message.type &&
						lastGroup.messages[0].moderationReason() ===
							message.moderationReason() &&
						Math.abs(
							new Date(lastGroup.messages[0].timestamp).getTime() -
								new Date(message.timestamp).getTime(),
						) < 120000
					) {
						return [
							...groups.slice(0, -1),
							{
								...lastGroup,
								messages: message.moderationReason()
									? [message]
									: [...lastGroup.messages, message],
							},
							...markerGroup,
						];
					} else {
						return [
							...groups,
							{
								senderId: message.moderationReason()
									? message.chatId()
									: message.senderId,
								threadId: message.threadId,
								incoming: message.isIncoming(),
								backInTime:
									!!lastRealGroup &&
									new Date(lastRealGroup.messages[0].timestamp).getTime() >
										new Date(message.timestamp).getTime(),
								type: message.type,
								messages: [message],
							},
							...markerGroup,
						];
					}
				},
				[] as (Group | "readUpToMarker")[],
			),
		[messages, readUpTo],
	);
	const throttledGroups = useThrottle(groups, 300);

	const messageGroupIndex = (messageId: string) => {
		for (const [idx, group] of throttledGroups.entries()) {
			if (
				group !== "readUpToMarker" &&
				group.messages.some((m) => [m.serverId, m.localId].includes(messageId))
			) {
				return idx;
			}
		}

		return null;
	};
	const hashIndex = location.hash.startsWith("#message:")
		? messageGroupIndex(location.hash.slice(9))
		: null;
	const initialTopId = initialTop?.serverId ?? initialTop?.localId;
	const prepended = initialTopId ? (messageGroupIndex(initialTopId) ?? 0) : 0;

	const [atBottom, setAtBottom] = useState(
		hashIndex === null || groups.length - hashIndex < 2,
	);

	const clearAttachments = () => {
		for (const attachment of attachments) {
			URL.revokeObjectURL(attachment.uri);
		}
		setAttachments([]);
		setAttachmentProgress(false);
	};

	const updateInputEmpty = () => {
		const newValue = (inputRef.current?.getLength() || 0) < 2;
		if (newValue !== inputEmpty) setInputEmpty(newValue);
	};

	const focusInput = useCallback(() => {
		const input = inputRef.current;
		if (input) {
			input.focus();
			setTimeout(() => input.setSelection(input.getLength()), 0);
		}
	}, [inputRef]);

	const onYReachStart = async () => {
		if (firstMessage.current && !loading.current) {
			loading.current = true;
			console.log("XYZZY onYReachStart", firstMessage.current);
			const newMessages = await activeChat.getMessagesBefore(
				firstMessage.current,
			);
			const newMembers = await membersForMessages(
				newMessages,
				(ids) => activeChat.getMemberDetails(ids),
				(id) => !members.has(id),
			);
			setMembers((prev) => new Map([...prev, ...newMembers]));
			setMessages((prev) => {
				const result = [...newMessages, ...prev];
				firstMessage.current = result[0];
				lastMessage.current = result.at(-1);
				return result;
			});
			loading.current = false;
		}
	};

	const onYReachEnd = async () => {
		if (
			!loading.current &&
			(!activeChat.lastMessage ||
				!lastMessage.current?.canReplace(activeChat.lastMessage))
		) {
			loading.current = true;
			console.log(
				"XYZZY onYReachEnd",
				activeChat.lastMessage,
				lastMessage.current,
			);
			const newMessages = await activeChat.getMessagesAfter(
				lastMessage.current ?? null,
			);
			console.log("XYZZY onYReachEnd 2", newMessages);
			if (newMessages.length < 1) {
				loading.current = false;
				return;
			}

			const newMembers = await membersForMessages(
				newMessages,
				(ids) => activeChat.getMemberDetails(ids),
				(id) => !members.has(id),
			);
			setMembers((prev) => new Map([...prev, ...newMembers]));

			setAtBottom(false);
			setMessages((prev) => {
				const result = [...prev, ...newMessages];
				firstMessage.current = result[0];
				lastMessage.current = result.at(-1);
				return result;
			});
			loading.current = false;
		}
	};

	const lastMessageFromList = messages.at(-1);
	const updateLastMessage = useEffectEvent(() => {
		// We use this fake aria-live because the virtual scrolling
		// messes up the normal way
		const senderId = lastMessage.current?.senderId;
		if (!senderId) return;

		const sender = members.get(senderId);
		document.getElementById("chat-messages-aria-live")!.textContent =
			"New message from " +
				sender?.displayName +
				". " +
				lastMessage.current?.body()?.toPlainText() || "";
	});
	useEffect(() => {
		updateLastMessage();
	}, [lastMessageFromList]);

	const hashChanged = useEffectEvent(async (messageId: string) => {
		if (!messageList.current) return;

		if (hashIndex) {
			messageList.current.scrollToIndex({ index: hashIndex });
		} else if (messageId) {
			const chat = activeChat;
			const message = await persistence.getMessage(
				client.accountId(),
				chat.chatId,
				messageId,
				null,
			);
			if (!message) throw "Did not find message: " + messageId;

			const got = await chat.getMessagesAround(message);
			if (got.length > 0) {
				const newMembers = await membersForMessages(
					got,
					(ids) => activeChat.getMemberDetails(ids),
					(id) => !members.has(id),
				);
				setMembers((prev) => new Map([...prev, ...newMembers]));

				// Loading to blank is not super helpful
				setMessages(got);
				setInitialTop(got[0]);
				firstMessage.current = got[0];
				lastMessage.current = got.at(-1);
				setAtBottom(false);
				setVirtuosoGeneration((g) => g + 1);
			}
		}
	});
	useEffect(() => {
		if (location.hash.startsWith("#message:")) {
			hashChanged(location.hash.slice(9));
		}
	}, [location]);

	useEffect(() => {
		if (!activeChat) return;

		const token = activeChat.addMembersUpdatedListener((updatedMembers) => {
			const newMembers: Array<[string, borogove.Member]> = updatedMembers.map(
				(member) => [member.id, member],
			);
			setMembers((existing) => new Map([...existing, ...newMembers]));
		});

		return () => activeChat.removeEventListener(token);
	}, [activeChat]);

	const newActiveConversation = useEffectEvent(async () => {
		setEditing(null);
		setReplyTo(null);

		const extras = getChatExtras(activeChat.chatId);
		inputRef.current?.setContents(extras?.draftDelta ?? []);
		setAttachments(extras?.attachments || []);
		setCurrentThread(extras?.currentThread);
		activeChat.setActive(true, extras?.currentThread ?? null);

		setTabIndex(extras?.tabIndex ?? 0);
		setTabNames(extras?.tabNames ?? []);
		const sessions = extras?.commandSessions ?? [];
		setCommandSessions(sessions);
		sessions.forEach((session, idx) =>
			session.then((s) =>
				setTabNames((existing) =>
					existing.map((value, i) => (i === idx ? s.name : value)),
				),
			),
		);

		setEditChatName(false);
		updateInputEmpty();
	});

	const cleanupConversation = useEffectEvent(() => {
		const draftText = (inputRef.current?.getText() || "").replace(/\s*\n/, "");
		const draftDelta = inputRef.current?.getContents();
		// scrollTo isn't really part of the user's draft but it's part of the
		// user's UI state and is needed in the same places
		// Use window.location to get any updates that we didn't tell router about
		const scrollTo =
			!atBottom && scrollTop.current ? scrollTop.current : undefined;
		setChatExtras(activeChat.chatId, {
			draftText,
			draftDelta,
			attachments,
			currentThread,
			scrollTo,
			tabIndex,
			tabNames,
			commandSessions,
		});

		activeChat.setActive(false, currentThread ?? null);
	});

	useEffect(() => {
		newActiveConversation();

		return () => {
			cleanupConversation();
		};
	}, [activeChat.chatId]);

	useEffect(() => {
		persistence
			.get(`ui#wallpaper/${activeChat?.chatId}`)
			.then((url) => setWallpaper(url ?? undefined));
	}, [activeChat.chatId, persistence]);

	useEffect(() => {
		focusInput();
	}, [focusInput, activeChat.chatId]);

	const onlineEvent = useEffectEvent(() => {
		onYReachEnd();
	});

	const newMessage = useEffectEvent(
		(message: borogove.ChatMessage, eventType: borogove.ChatMessageEvent) => {
			if (message.chatId() !== activeChat.chatId) return;

			(async () => {
				const newMembers = await membersForMessages(
					[message],
					(ids) => activeChat.getMemberDetails(ids),
					(id) => !members.has(id),
				);
				setMembers((prev) => new Map([...prev, ...newMembers]));

				setMessages((messages) => {
					const updateIdx = messages.findIndex((m) => message.canReplace(m));
					console.log("XYZZY live message", message, eventType, updateIdx, [
						...messages,
					]);
					if (updateIdx > -1) {
						const result = [
							...messages.slice(0, updateIdx),
							message,
							...messages.slice(updateIdx + 1),
						];
						firstMessage.current = result[0];
						lastMessage.current = result.at(-1);
						return result;
					}

					if (eventType !== borogove.ChatMessageEvent.DeliveryEvent)
						return messages;

					firstMessage.current = messages[0] ?? message;
					lastMessage.current = message;
					return [...messages, message];
				});
			})();
		},
	);

	useEffect(() => {
		const messageToken = client.addChatMessageListener(newMessage);
		const onlineToken = client.addStatusOnlineListener(onlineEvent);

		return () => {
			client.removeEventListener(messageToken);
			client.removeEventListener(onlineToken);
		};
	}, [client]);

	const addCommandSession = (
		name: string,
		session: Promise<borogove.CommandSession>,
	) => {
		setTabNames((existing) => existing.concat([name]));
		setCommandSessions((existing) => {
			const idx = existing.length;
			setTabIndex(idx + 1);
			session.then((s) =>
				setTabNames((existing) =>
					existing.map((value, i) => (i === idx ? s.name : value)),
				),
			);
			return existing.concat([session]);
		});
	};

	const updateCommandSession = (
		idx: number,
		session: Promise<borogove.CommandSession> | undefined,
	) => {
		if (session === undefined) {
			setTabIndex(0);
		} else {
			session.then((s) =>
				setTabNames((existing) =>
					existing.map((value, i) => (i === idx ? s.name : value)),
				),
			);
		}
		setCommandSessions((existing) =>
			existing
				.map((value, i) => (i === idx ? session : value))
				.filter((v) => !!v),
		);
	};

	const setSelectedThread = (threadId: string | null) => {
		activeChat.setActive(true, threadId);
		setCurrentThread(threadId ?? undefined);
	};

	const avatarClick = (group: Group) => {
		if (group.type !== borogove.MessageType.MessageChannelPrivate) {
			setSelectedThread(group.messages[0].threadId);
		}

		const member = members.get(group.senderId);
		if (member) setPopupMember(member);
	};

	const addMention = useCallback(
		(member: borogove.Member) => {
			if (!inputRef.current) return;

			const mention = inputRef.current.getModule("mention") as Mention;
			const displayName = member.displayName;
			const sel = inputRef.current.getSelection();
			const colonBefore = sel
				? inputRef.current.getText(sel.index - 2, 2).indexOf(":")
				: -1;
			if (inputRef.current.getLength() < 2) {
				mention.insertItem(
					{ denotationChar: "@", id: member.id, value: displayName },
					true,
					{ spaceAfterInsert: false },
				);
				inputRef.current.insertText(inputRef.current.getLength() - 1, ": ");
			} else if (
				sel &&
				inputRef.current.getText(sel.index, 2).indexOf(":") >= 0
			) {
				inputRef.current.insertText(sel.index, ", ");
				inputRef.current.setSelection(sel.index + 2);
				mention.insertItem(
					{ denotationChar: "@", id: member.id, value: displayName },
					true,
					{ spaceAfterInsert: false },
				);
			} else if (sel && colonBefore >= 0) {
				const start = inputRef.current.getLength() - 1 - (2 - colonBefore);
				inputRef.current.insertText(start, ", ");
				inputRef.current.setSelection(start + 2);
				mention.insertItem(
					{ denotationChar: "@", id: member.id, value: displayName },
					true,
					{ spaceAfterInsert: false },
				);
				inputRef.current.setSelection(sel.index + 3);
			} else {
				mention.insertItem(
					{ denotationChar: "@", id: member.id, value: displayName },
					true,
				);
			}
		},
		[inputRef],
	);

	useEffect(() => {
		innerChatRef.current.addMention = addMention;
	}, [innerChatRef, addMention]);

	const toggleDetails = useCallback(() => {
		if (location.pathname.endsWith("/details")) {
			searchParams.delete("memberQ");
			navigate(
				href(location.pathname.replace(/\/details$/, ""), {}, searchParams),
			);
		} else {
			navigate(href("./details", {}, searchParams));
		}
	}, [location, searchParams, navigate]);

	const handleChange = () => {
		activeChat.typing(
			currentThread ?? null,
			inputRef.current?.getText() ?? null,
		);
		updateInputEmpty();
	};

	const membersPromise = useRef<Promise<Fuse<borogove.Member>> | null>(null);
	const mentionHandler = useCallback(
		(
			searchTerm: string,
			renderList: (
				matches: {
					id: string;
					value: string;
					[key: string]: string | undefined;
				}[],
			) => void,
			mentionChar: string,
		) => {
			if (!activeChat) return;

			if (!membersPromise.current) {
				membersPromise.current = activeChat.members().then((members) => {
					const filtered = members
						.filter((m) => !m.roles.find((r) => r.id === "outcast"))
						.map((m: borogove.Member & { __fuseKey?: string }) => {
							// Fuse penalizes whitespace otherwise
							m.__fuseKey = m.displayName.replace(/\s+/g, "");
							return m;
						});
					return new Fuse(filtered, {
						useExtendedSearch: true,
						threshold: 0.4,
						keys: ["__fuseKey"],
						sortFn: (
							a: { score: number; idx: number },
							b: { score: number; idx: number },
						): number => {
							// sort by match, but only if presence differs
							if (
								filtered[a.idx].showPresence === filtered[b.idx].showPresence
							) {
								return a.score === b.score
									? a.idx < b.idx
										? -1
										: 1
									: a.score < b.score
										? -1
										: 1;
							}

							// Otherwise sort by presence, more available higher
							return (
								filtered[a.idx].showPresence - filtered[b.idx].showPresence
							);
						},
					});
				});
			}

			membersPromise.current.then((fuse) => {
				if (mentionChar === "@" && fuse.getIndex().size() > 2) {
					renderList(
						fuse
							.search(searchTerm)
							.map((x) => ({ id: x.item.id, value: x.item.displayName })),
					);
				} else {
					renderList([]);
				}
			});
		},
		[activeChat],
	);

	const mentionClose = useCallback(() => {
		membersPromise.current = null;
		return true;
	}, []);

	const triggerRecentEdit = () => {
		const sentMessage = messages.findLast((m) => !m.isIncoming());
		if (!sentMessage) return;

		editMessage(sentMessage);
	};

	const handleSend = () => {
		if (!activeChat) return;
		if (!inputRef.current) return;

		focusInput();

		const inputEmpty = (inputRef.current?.getLength() || 0) < 2;
		if (inputEmpty && attachments.length < 1) return;

		const body = inputRef.current.getSemanticHTML().replace(/&nbsp;/g, " "); // Quill replaces *every* space with nbsp...

		const message = replyTo
			? replyTo.reply()
			: new borogove.ChatMessageBuilder();
		message.threadId = currentThread || message.threadId;
		for (const { attachment } of attachments) {
			message.addAttachment(attachment);
		}
		if (body !== null && body !== undefined) {
			message.setBody(borogove.Html.fromString(body));
		}

		if (editing) {
			activeChat.correctMessage(editing, message);
			setEditing(null);
		} else {
			activeChat.sendMessage(message);
		}

		inputRef.current?.setText("");
		clearAttachments();
		setReplyTo(null);
	};

	const attachFiles = async (files: File[]) => {
		setAttachmentProgress(true);
		await Promise.all(
			files.map((file) =>
				client.prepareAttachment(file).then((attachment) => {
					if (attachment === null) throw "Attachment failed";

					setAttachments((existing) => [
						...existing,
						{ attachment, uri: URL.createObjectURL(file) },
					]);
				}),
			),
		);
		setAttachmentProgress(false);
	};

	const handleAttach = () => {
		const input = document.createElement("input");
		input.type = "file";
		input.multiple = true;
		input.addEventListener("change", () => {
			if (!input.files || input.files.length < 1) return; // Nothing selected
			attachFiles(Array.from(input.files));
		});
		input.click();
	};

	const removeAttachment = (toRemove: Attachment) => {
		URL.revokeObjectURL(toRemove.uri);
		setAttachments(
			attachments.filter((attachment) => attachment.uri !== toRemove.uri),
		);
	};

	const editMessage = (m: borogove.ChatMessage) => {
		if (!inputRef.current) return;

		// Sanitize using DOM in-place to prevent re-parsing
		const contentFragment = m.body().asDOM();
		DOMPurify.sanitize(contentFragment);
		// But Quill still wants HTML string for the dangerouslyPaste...
		const content = Array.from(contentFragment.childNodes)
			.map((node) =>
				node instanceof Element ? node.outerHTML : node.nodeValue,
			)
			.join("");
		setAttachments(
			m.attachments.map((attachment) => ({
				attachment,
				uri: attachment.uris[0],
			})),
		);
		setReplyTo(m.replyToMessage);
		setCurrentThread(m.threadId ?? undefined);
		setEditing(m);
		inputRef.current.clipboard.dangerouslyPasteHTML(content);
		setTimeout(() => {
			if (!inputRef.current) return;

			inputRef.current.focus();
			inputRef.current.setSelection(inputRef.current.getLength());
		}, 100);
	};

	const setupReply = (m: borogove.ChatMessage) => {
		setReplyTo(m);
		setTimeout(() => {
			if (!inputRef.current) return;

			inputRef.current.focus();
			inputRef.current.setSelection(inputRef.current.getLength());
		}, 100);
	};

	const getTypingIndicator = () => {
		return typingMembers.map((typingMember) => {
			return (
				<TypingIndicator
					key={`typing-${typingMember.id}`}
					content={`${typingMember.displayName} is typing`}
				/>
			);
		});
	};

	const waitForImagesLoaded = useCallback(() => {
		const content = messageListContainer.current;
		if (!content) return Promise.resolve();

		const images = content.getElementsByTagName("img");
		if (images.length === 0) return Promise.resolve();

		const promises = Array.from(images).map((img: HTMLImageElement) => {
			if (img.complete) return Promise.resolve();
			return new Promise((resolve) => {
				img.onload = resolve;
				img.onerror = resolve;
			});
		});

		return Promise.all(promises);
	}, []);

	const mostRecentMessageRef = useRef<borogove.ChatMessage | undefined>(
		undefined,
	);
	const markRead = useCallback(() => {
		if (document.hidden || !document.hasFocus()) return;
		const mostRecentMessage = mostRecentMessageRef.current;
		if (!mostRecentMessage) return;
		setTimeout(async () => {
			activeChat.markReadUpTo(mostRecentMessage);
			const tag = client.accountId() + ">" + activeChat.chatId;
			if (navigator.serviceWorker) {
				const serviceWorkerRegistration = await navigator.serviceWorker.ready;
				if (serviceWorkerRegistration.getNotifications) {
					const notifs = await serviceWorkerRegistration.getNotifications({
						tag,
					});
					notifs.forEach((notif) => notif.close());
				}
			}
		}, 0);
	}, [activeChat, client]);

	const onItemsRendered = (
		items: Array<{ data?: Group | "readUpToMarker" }>,
	) => {
		const topMessage =
			items[0]?.data === "readUpToMarker"
				? null
				: items[0]?.data?.messages?.[0];
		const topId = topMessage?.serverId || topMessage?.localId;
		const newHash = `#message:${topId}`;
		if (topId && window.location.hash !== newHash) {
			// The __nuqs__ prevents nuqs from interpreting this
			try {
				history.replaceState(null, "__nuqs__", newHash);
			} catch {}
			scrollTop.current = topId;
		}

		const showingMessages: borogove.ChatMessage[] = (
			items.findLast((g) =>
				g.data === "readUpToMarker"
					? {}
					: g.data?.messages?.findLast((m: borogove.ChatMessage) => m.serverId),
			)?.data as Group
		)?.messages;
		const mostRecentMessage = showingMessages
			? showingMessages.findLast((m) => m.serverId)
			: undefined;
		mostRecentMessageRef.current = mostRecentMessage;
		markRead();
	};

	useEffect(() => {
		window.addEventListener("visibilitychange", markRead);

		return () => {
			window.removeEventListener("visibilitychange", markRead);
		};
	}, [markRead]);

	const jumpToBottom = async () => {
		if (
			!activeChat.lastMessage ||
			!lastMessage.current?.canReplace(activeChat.lastMessage)
		) {
			// We haven't loaded all messages so we need to load not just scroll
			const loaded = await activeChat.getMessagesBefore(null);
			firstMessage.current = loaded[0];
			lastMessage.current = loaded.at(-1);

			const newMembers = await membersForMessages(
				loaded,
				(ids) => activeChat.getMemberDetails(ids),
				(id) => !members.has(id),
			);
			setMembers((prev) => new Map([...prev, ...newMembers]));

			setMessages(loaded);
			setInitialTop(loaded[0]);
			setAtBottom(true);
		} else {
			messageList.current?.scrollToIndex({ index: Number.MAX_SAFE_INTEGER });
		}
	};

	const onAtBottomStateChange = (nowAtBottom: boolean) => {
		if (
			!nowAtBottom ||
			(activeChat.lastMessage &&
				lastMessage.current?.canReplace(activeChat.lastMessage))
		)
			setAtBottom(nowAtBottom);
	};

	useEffect(() => {
		if (atBottom) {
			(async () => {
				await waitForImagesLoaded();
				setTimeout(() => {
					console.log("XYZZY force to bottom", atBottom);
					messageList.current?.scrollToIndex({
						index: Number.MAX_SAFE_INTEGER,
					});
				}, 400);
			})();
		}
	}, [messages.length, atBottom, waitForImagesLoaded]);

	const headerId = useId();

	return (
		<>
			<ChatContainer
				className={active ? "active" : undefined}
				aria-labelledby={headerId}
				role="main"
			>
				<ConversationHeader>
					<ConversationHeader.Back onClick={() => navigate("../")} />
					<AvatarWithPlaceholder
						as={Avatar}
						photoUri={activeChat.getPhoto()}
						placeholderUri={activeChat.getPlaceholder()}
						displayName={activeChat.getDisplayName()}
						onClick={() => toggleDetails()}
						style={{ cursor: "pointer" }}
						aria-hidden="true"
					/>
					<ConversationHeader.Content>
						{!editChatName ? (
							<h1
								onClick={() => toggleDetails()}
								tabIndex={-1}
								className="cs-conversation-header__user-name"
								id={headerId}
							>
								{activeChat.getDisplayName()}
							</h1>
						) : (
							<input
								id={headerId}
								type="text"
								defaultValue={activeChat.getDisplayName()}
								autoFocus={true}
								onBlur={() => setEditChatName(false)}
								onKeyPress={(e) => {
									if (e.key === "Enter") {
										activeChat.setDisplayName(e.currentTarget.value);
										setEditChatName(false);
									}
								}}
							/>
						)}
						{!editChatName &&
							activeChat instanceof borogove.Channel &&
							(editSubject ? (
								<textarea
									defaultValue={activeChat.subject()}
									autoFocus={true}
									onBlur={() => setEditSubject(false)}
									rows={activeChat.subject().split(/\n/).length + 1}
									onKeyPress={(e) => {
										if (e.key === "Enter" && !e.shiftKey) {
											activeChat.setSubject(e.currentTarget.value);
											setEditSubject(false);
										}
									}}
								/>
							) : (
								!groups.find(
									(g) => g !== "readUpToMarker" && g.threadId === subjectBoxId,
								) && (
									<div
										className="pinned"
										onClick={(e) => {
											if (e.currentTarget.closest("a")) return;
											setEditSubject(activeChat.canSetSubject());
										}}
									>
										<Linkify
											options={{ defaultProtocol: "https", truncate: 74 }}
										>
											{activeChat.subject()}
											{activeChat.canSetSubject() && " 🖉"}
										</Linkify>
									</div>
								)
							))}
					</ConversationHeader.Content>
					<ConversationHeader.Actions>
						{!isInvite && (
							<Button
								title="Edit chat name"
								icon={<FontAwesomeIcon icon={faPencil} />}
								onClick={() => setEditChatName(true)}
							/>
						)}
						{!activeChat.isTrusted() && (
							<AddUserButton
								title="Trust this chat"
								onClick={() => {
									activeChat.setTrusted(true);
									activeChat.bookmark();
								}}
							/>
						)}
						{activeChat.callStatus() === borogove.calls.CallStatus.NoCall &&
							activeChat.canVideoCall() && (
								<Button
									title="Start video call"
									icon={
										<FontAwesomeIcon
											icon={faVideo}
											onClick={() => activeChat.startCall(true, true)}
										/>
									}
								/>
							)}
						{activeChat.callStatus() === borogove.calls.CallStatus.NoCall &&
							activeChat.canAudioCall() && (
								<Button
									title="Start audio call"
									icon={
										<FontAwesomeIcon
											icon={faPhone}
											onClick={() => activeChat.startCall(true, false)}
										/>
									}
								/>
							)}
						<Button
							title="Show chat details"
							icon={<FontAwesomeIcon icon={faCircleInfo} />}
							onClick={() => toggleDetails()}
						/>
					</ConversationHeader.Actions>
				</ConversationHeader>
				<div
					as={MessageList}
					className="cs-message-list"
					ref={messageListContainer}
					style={{
						background: wallpaper
							? `url(${wallpaper}) center / cover no-repeat`
							: "inherit",
					}}
				>
					{commandSessions.length > 0 && (
						<Tabs
							value={tabIndex}
							variant="fullWidth"
							onChange={(e, idx) => setTabIndex(idx)}
						>
							<Tab label="Chat" />
							{commandSessions.map((session, idx) => (
								<Tab key={idx} label={tabNames[idx]} />
							))}
						</Tabs>
					)}
					{tabIndex > 0 && (
						<CommandSession
							session={commandSessions[tabIndex - 1]}
							updateSession={updateCommandSession}
							idx={tabIndex - 1}
						/>
					)}
					<Virtuoso
						style={{ display: tabIndex === 0 ? "block" : "none" }}
						aria-label="Chat Messages"
						key={virtuosoGeneration}
						ref={messageList}
						followOutput={() => atBottom}
						alignToBottom={true}
						startReached={onYReachStart}
						endReached={onYReachEnd}
						atBottomThreshold={60}
						atBottomStateChange={onAtBottomStateChange}
						itemsRendered={onItemsRendered}
						firstItemIndex={Math.floor(Number.MAX_SAFE_INTEGER / 2) - prepended}
						initialTopMostItemIndex={{ index: hashIndex ?? "LAST" }}
						data={throttledGroups}
						itemContent={(index, g: Group | "readUpToMarker") => {
							if (g === "readUpToMarker") {
								return (
									<Divider
										key="readUpToMarker"
										variant="middle"
										textAlign="center"
									>
										New messages
									</Divider>
								);
							} else if (g.threadId === subjectBoxId) {
								return (
									<div id="welcome-box">
										<Linkify
											as="div"
											options={{ defaultProtocol: "https", truncate: 74 }}
										>
											{g.messages[0].subject()}
										</Linkify>
									</div>
								);
							} else {
								const user = members.get(g.senderId)!;
								return (
									<MessageGroup
										className={g.backInTime ? "back-in-time" : ""}
										direction={g.incoming ? "incoming" : "outgoing"}
									>
										{(g.senderId !== activeChat.chatId ||
											(g.senderId !== activeChat.chatId && g.incoming)) && (
											<AvatarWithPlaceholder
												as={Avatar}
												photoUri={user?.photoUri}
												placeholderUri={user?.placeholderUri}
												displayName={user?.displayName}
												onMouseDown={(e) => e.preventDefault()}
												onClick={() => avatarClick(g)}
												onKeyPress={(e) => {
													if (e.key === "Enter" || e.key === " ") {
														avatarClick(g);
													}
												}}
												tabIndex={0}
												aria-label={"Open profile for " + user?.displayName}
											/>
										)}
										<MessageGroup.Messages>
											{g.messages.map((m: borogove.ChatMessage, mIdx) =>
												m.type === borogove.MessageType.MessageCall ? (
													<CallMessage key={m.serverId || m.localId} m={m} />
												) : (
													<MessageBubble
														key={m.serverId || m.localId}
														chat={activeChat}
														position={
															g.messages.length === 1
																? "single"
																: mIdx >= g.messages.length - 1
																	? "last"
																	: mIdx < 1
																		? "first"
																		: "normal"
														}
														className={`${activeChat instanceof borogove.Channel && m.senderId === activeChat.chatId ? "system" : ""} ${replyTo === m ? "highlight" : ""}`}
														client={client}
														members={members}
														setSelectedThread={setSelectedThread}
														m={m}
													>
														{m.type !==
															borogove.MessageType.MessageChannelPrivate && (
															<MessageMenuInChat
																as={Message.Header}
																setReplyTo={setupReply}
																editMessage={editMessage}
																setSelectedThread={setSelectedThread}
																m={m}
																chat={activeChat}
															/>
														)}
													</MessageBubble>
												),
											)}
										</MessageGroup.Messages>
									</MessageGroup>
								);
							}
						}}
					/>
					{tabIndex === 0 && (
						<>
							{getTypingIndicator()}
							<Button
								title="Jump to bottom"
								className={"jump-to-bottom " + (atBottom ? "" : "visible")}
								{...{ inert: atBottom ? "" : undefined }}
								icon={
									<FontAwesomeIcon
										icon={faAnglesDown}
										onClick={() => jumpToBottom()}
									/>
								}
							/>
						</>
					)}
					{isInvite && (
						<div className="invite-approve">
							{invitedBy && <p>Invited by {invitedBy.displayName}</p>}
							{activeChat instanceof borogove.Channel ||
							messageRequests === "strangers" ? (
								<Button
									border
									onClick={() => {
										activeChat.setTrusted(true);
										activeChat.bookmark();
									}}
								>
									{activeChat instanceof borogove.Channel ? "Join" : "Approve"}
								</Button>
							) : (
								<Button border onClick={() => activeChat.bookmark()}>
									Accept
								</Button>
							)}
							{activeChat instanceof borogove.Channel &&
								activeChat.uiState === borogove.UiState.Invited && (
									<Button border onClick={() => activeChat.close()}>
										Decline
									</Button>
								)}
							<Button border onClick={() => activeChat.block()}>
								Block{" "}
								{activeChat instanceof borogove.Channel &&
									activeChat.uiState === borogove.UiState.Invited &&
									"Inviter"}
							</Button>
							<Button
								border
								className="spam"
								onClick={() => activeChat.block(true, activeChat.lastMessage)}
							>
								Report SPAM
							</Button>
							<p>
								{activeChat instanceof borogove.Channel
									? "Join"
									: messageRequests === "strangers"
										? "Approve"
										: "Accept"}{" "}
								to continue chatting. If blocked, the contact will not be able
								to reach you.
							</p>
						</div>
					)}
				</div>

				{tabIndex === 0 && !isInvite && (
					<div as={MessageInput} id="message-input-root">
						{replyTo && (
							<div className="reply">
								<span>
									Replying to{" "}
									<span className="h-card">
										{members.get(replyTo.senderId)?.displayName}
									</span>
								</span>
								<Button
									onClick={() => {
										setReplyTo(null);
										setSelectedThread(null);
									}}
									title="Cancel reply"
									icon={<FontAwesomeIcon icon={faCircleXmark} />}
								/>
							</div>
						)}
						{(attachmentProgress || attachments.length > 0) && (
							<div className="attachments">
								{attachments.map((attachment) => (
									<img
										crossOrigin="anonymous"
										key={attachment.uri}
										src={attachment.uri}
										alt={attachment.attachment.name ?? undefined}
										title="Click to remove"
										onClick={() => removeAttachment(attachment)}
									/>
								))}
								{attachmentProgress && <CircularProgress />}
							</div>
						)}
						<div className="cs-message-input">
							<div className="cs-message-input__tools">
								{activeChat.hasCommands() && (
									<CommandMenu
										chat={activeChat}
										addSession={addCommandSession}
									/>
								)}
								{activeChat.canSend() && (
									<>
										<Button
											onClick={() => setSelectedThread(uuidv7())}
											title="Thread to send this message to, tap to start new"
											icon={
												currentThread ? (
													<img
														src={borogove.Identicon.svg(currentThread ?? "")}
														alt="Thread icon"
														className="svg-inline--fa"
													/>
												) : (
													<SpoolIcon />
												)
											}
										/>
										<AttachmentButton
											title="Attach media to message"
											onClick={handleAttach}
										/>
									</>
								)}
							</div>
							{activeChat.canSend() ? (
								<>
									<Quill
										aria-label="Compose message"
										className="cs-message-input__content-editor-wrapper"
										ref={inputRef}
										toolbar={richTextToolbar}
										onTextChange={handleChange}
										triggerRecentEdit={triggerRecentEdit}
										enterIsSend={enterIsSend && handleSend}
										mentionHandler={mentionHandler}
										mentionClose={mentionClose}
										attachFiles={attachFiles}
										placeholder="Send message"
									/>
									{!editing && (
										<div className="cs-message-input__tools">
											<SendButton
												title="Send message"
												onClick={handleSend}
												disabled={inputEmpty && attachments.length < 1}
											/>
										</div>
									)}
								</>
							) : (
								activeChat instanceof borogove.Channel && (
									<Button
										style={{ marginLeft: "auto" }}
										border
										onClick={() => {
											activeChat.requestToSend();
											setRequestToSpeakSent(true);
										}}
										disabled={requestToSpeakSent}
									>
										{requestToSpeakSent ? "Request Sent" : "Request to Speak"}
									</Button>
								)
							)}
						</div>
						{editing && (
							<div className="edit-tools">
								<small>editing</small>
								<Button
									border
									onClick={() => {
										setEditing(null);
										inputRef.current?.setText("");
										clearAttachments();
									}}
								>
									Cancel
								</Button>
								<Button
									border
									onClick={handleSend}
									disabled={inputEmpty && attachments.length < 1}
								>
									Save
								</Button>
							</div>
						)}
					</div>
				)}
			</ChatContainer>
			<Outlet context={{ setWallpaper, ...outletContext }} />
		</>
	);
};

export const Chat = () => {
	const { client, activeChat, persistence } = useOutletContext<{
		client: borogove.Client;
		activeChat: borogove.Chat;
		persistence: borogove.persistence.KeyValueStore & borogove.Persistence;
	}>();

	const getMessages = async () => {
		const messageId = location.hash.startsWith("#message:")
			? location.hash.slice(9)
			: null;
		const chat = activeChat;
		const readUpTo = await chat.readUpTo();
		const invitedBy = await chat.invitedBy();
		const message =
			messageId &&
			(await persistence.getMessage(
				client.accountId(),
				chat.chatId,
				messageId,
				null,
			));
		if (message) {
			console.log(
				"LOADING JUMP TO",
				message.timestamp,
				message.serverId,
				message,
			);
			const messages = await chat.getMessagesAround(message);
			const members = await membersForMessages(
				messages,
				(ids) => chat.getMemberDetails(ids),
				() => true,
			);
			if (messages.length > 0)
				return { messages, members, readUpTo, invitedBy };
		} else {
			console.log("LOADING NO JUMP TO, LOAD END");
		}

		const messages = await chat.getMessagesBefore(null);
		const members = await membersForMessages(
			messages,
			(ids) => chat.getMemberDetails(ids),
			() => true,
		);
		if (activeChat instanceof borogove.Channel && activeChat.subject()) {
			if (!(await activeChat.readUpTo())) {
				messages.push(
					new borogove.ChatMessageBuilder({
						subject: activeChat.subject(),
						senderId: activeChat.chatId,
						localId: "subject",
						direction: borogove.MessageDirection.MessageReceived,
						threadId: subjectBoxId,
					}).build(),
				);
			}
		}
		return { messages, members, readUpTo, invitedBy };
	};

	return (
		<AwaitOnce
			key={activeChat.chatId}
			fallback={<CircularProgress />}
			resolve={getMessages}
		>
			<ChatInner />
		</AwaitOnce>
	);
};
